// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Finding Card - displays a single contract finding with risk score,
 * text comparison, and KB panel.
 */
import React from 'react'
import { cn, Card, CardHeader, CardContent, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui'
import { FileText, Download, BookOpen } from 'lucide-react'
import { TextComparisonBox } from './primitives/TextComparisonBox'
import { CollapsibleKBPanel } from './primitives/CollapsibleKBPanel'
import { extractMarkdownTitle, getRiskLevel, getRiskBarClass } from '@/applications/contract-review/utils'
import { getRedlinePdfUrl, downloadFromUrl } from '@/applications/contract-review/utils/pdf-actions'
import type { ContractFinding } from '@/applications/contract-review/types'
import { useServices } from '@/app-shell/services'
import { getPageViewUrl } from '@/subsystems/documents'

interface Props {
  finding: ContractFinding
  className?: string
  documentId?: string
  orgId?: string
  workspaceId?: string
  /** Agent owning the dataset — its key is only unique within one. */
  agentId?: string
  datasetKey?: string
  version?: number
  /** Run this finding came from — identifies the redline that annotates it. */
  run?: number
  findingIndex?: number
  documentName?: string
}

export const FindingCard: React.FC<Props> = ({
  finding,
  className,
  documentId,
  orgId,
  workspaceId,
  agentId,
  datasetKey,
  version,
  run,
  findingIndex,
  documentName,
}) => {
  const services = useServices()
  const title = extractMarkdownTitle(finding.finding, 'Finding')
  const displayScore = typeof finding.riskScore === 'number' ? finding.riskScore : '—'

  const canLinkToPage = Boolean(documentId && orgId && workspaceId && typeof finding.pageNumber === 'number')
  const canRedline = canLinkToPage && agentId && datasetKey && typeof findingIndex === 'number'

  const handleOpenPage = async (pageNumber: number) => {
    if (!documentId || !orgId || !workspaceId) return
    try {
      const apiClient = services.getClient()
      const url = await getPageViewUrl(apiClient, orgId, workspaceId, documentId, pageNumber, { version })
      window.open(url, '_blank')
    } catch (err) {
      console.error('[FindingCard] Failed to get page view URL:', err)
    }
  }

  const handleOpenRedline = async () => {
    if (!documentId || !orgId || !workspaceId || !agentId || !datasetKey) return
    try {
      const url = await getRedlinePdfUrl(services.getClient(), orgId, workspaceId, documentId, agentId, datasetKey, { version, run })
      window.open(url, '_blank')
    } catch (err) {
      console.error('[FindingCard] Failed to get redline URL:', err)
    }
  }

  const handleDownloadRedline = async () => {
    if (!documentId || !orgId || !workspaceId || !agentId || !datasetKey) return
    try {
      const url = await getRedlinePdfUrl(
        services.getClient(), orgId, workspaceId, documentId, agentId, datasetKey,
        { download: true, version, run },
      )
      const baseName = documentName?.replace(/\.pdf$/i, '') ?? documentId
      downloadFromUrl(url, `${baseName}-annotated.pdf`)
    } catch (err) {
      console.error('[FindingCard] Failed to download redline:', err)
    }
  }

  const riskLevel = getRiskLevel(finding.riskScore)

  return (
    <Card data-testid={`cr-finding-${findingIndex ?? 0}`} className={cn(
      'relative bg-card text-card-foreground border',
      className
    )}>
      {/* Risk indicator bar */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-1 rounded-l-lg', getRiskBarClass(riskLevel))} />

      <CardHeader className="pb-4 pt-5 pl-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-3.5 flex-wrap">
              <span className="text-xs text-muted-foreground/60 font-mono">
                {finding.kbItemId}
              </span>
              {finding.sectionReference && (
                <>
                  <span className="text-muted-foreground/30">•</span>
                  <span className="text-xs text-muted-foreground/60">
                    § {finding.sectionReference}
                  </span>
                </>
              )}
              {typeof finding.pageNumber === 'number' && (
                <>
                  <span className="text-muted-foreground/30">•</span>
                  {canLinkToPage ? (
                    <TooltipProvider delayDuration={200}>
                      <div className="inline-flex items-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => void handleOpenPage(finding.pageNumber!)}
                              className="inline-flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-primary transition-colors"
                            >
                              <FileText className="h-3 w-3" />
                              <span className="hover:underline underline-offset-2">Page {finding.pageNumber}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">View page with annotation</TooltipContent>
                        </Tooltip>
                        {canRedline && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => void handleOpenRedline()}
                                  className="p-1 text-muted-foreground/50 hover:text-orange-500 transition-colors"
                                >
                                  <BookOpen className="h-3 w-3" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">View full redlined document</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => void handleDownloadRedline()}
                                  className="p-1 text-muted-foreground/50 hover:text-orange-500 transition-colors"
                                >
                                  <Download className="h-3 w-3" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">Download redlined PDF</TooltipContent>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </TooltipProvider>
                  ) : (
                    <span className="text-xs text-muted-foreground/60">Page {finding.pageNumber}</span>
                  )}
                </>
              )}
            </div>
            <h3 className="text-lg font-semibold leading-snug text-foreground">
              {title}
            </h3>
          </div>
          <div className="flex flex-col items-center shrink-0 min-w-[56px]">
            <span className="text-3xl font-bold tabular-nums leading-none text-foreground">
              {displayScore}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50 mt-1">
              Risk
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-0 pl-5 pb-5">
        {finding.rationale && (
          <div className="text-sm text-muted-foreground/80 italic leading-relaxed pl-4 py-2 border-l-2 border-slate-200 dark:border-slate-700">
            {finding.rationale}
          </div>
        )}

        <TextComparisonBox
          originalText={finding.currentText ?? undefined}
          changeType={finding.changeType}
          changeText={finding.changeText}
          targetPhrase={finding.targetPhrase}
          layout="horizontal"
        />

        <CollapsibleKBPanel
          kbItemId={finding.kbItemId}
          kbRiskScore={finding.riskScore}
          content={finding.finding}
        />
      </CardContent>
    </Card>
  )
}
